import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { GlassCard } from '../components/ui/glass-card';
import { apiClient, axiosInstance } from '../services/apiClient';
import { AcceptInviteRequest } from '../generated/api-client';
import toast from 'react-hot-toast';
import PasswordRequirementsChecklist from '../components/PasswordRequirementsChecklist';
import { describeMissingRequirements, validatePassword } from '../utils/passwordPolicy';

const InvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [token, setToken] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<{ email: string; role: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Token di invito non fornito');
      setLoading(false);
      return;
    }

    setToken(tokenParam);
    fetchInviteInfo(tokenParam);
  }, [searchParams]);

  const fetchInviteInfo = async (token: string) => {
    try {
      const response = await axiosInstance.get(`/auth/invite?token=${encodeURIComponent(token)}`);
      if (response.data.valid) {
        setInviteInfo({
          email: response.data.email,
          role: response.data.role,
        });
      } else {
        setError(response.data.error || 'Token non valido o scaduto');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Errore nel caricamento dell'invito");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error('Token mancante');
      return;
    }

    if (!firstname.trim()) {
      toast.error('Inserisci il nome');
      return;
    }
    if (!lastname.trim()) {
      toast.error('Inserisci il cognome');
      return;
    }
    if (!password.trim()) {
      toast.error('Inserisci la password');
      return;
    }
    if (!passwordConfirm.trim()) {
      toast.error('Conferma la password');
      return;
    }
    if (password !== passwordConfirm) {
      toast.error('Le password non coincidono');
      return;
    }
    const missingRequirements = describeMissingRequirements(password);
    if (missingRequirements) {
      toast.error(missingRequirements);
      return;
    }

    setSubmitting(true);

    try {
      const request = new AcceptInviteRequest({
        token,
        password,
        password_confirm: passwordConfirm,
        firstname: firstname.trim(),
        lastname: lastname.trim(),
      });
      await apiClient.accept_invite_auth_invite_post(request);
      toast.success('Registrazione completata! Ora puoi accedere.');
      navigate('/login');
    } catch (err: any) {
      const errorMsg = err?.response?.data?.detail || "Errore nell'accettazione dell'invito";
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const primaryColor = theme.palette.primary.main;
  const primaryLightColor = theme.palette.primary.light;

  const getRoleLabel = (role: string) => {
    const roleMap: { [key: string]: string } = {
      baseUser: 'Utente Base',
      mantainer: 'Mantainer',
      admin: 'Amministratore',
    };
    return roleMap[role] || role;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        overflow: 'auto',
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* sfere sfumate */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '192px',
          height: '192px',
          borderRadius: '50%',
          backgroundColor: 'rgba(0, 161, 156, 0.1)',
          filter: 'blur(60px)',
          zIndex: 1,
          animation: 'pulse 3s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 0.5 },
            '50%': { opacity: 0.8 },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '288px',
          height: '288px',
          borderRadius: '50%',
          backgroundColor: 'rgba(146, 214, 213, 0.1)',
          filter: 'blur(60px)',
          zIndex: 1,
          animation: 'pulse 3s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 0.5 },
            '50%': { opacity: 0.8 },
          },
        }}
      />

      <GlassCard
        hover={false}
        style={{
          width: '100%',
          maxWidth: '28rem',
          padding: '1rem',
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Button
          variant="text"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/login')}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            color: theme.palette.text.primary,
            minWidth: 'auto',
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
            EDAI
          </Typography>
        </Box>

        <Typography
          variant="h5"
          component="h1"
          align="center"
          sx={{
            mb: 4,
            fontWeight: 600,
            background: `linear-gradient(135deg, ${primaryColor}, ${primaryLightColor})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Accetta Invito
        </Typography>

        {loading ? (
          <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <CircularProgress sx={{ mx: 'auto', color: primaryColor }} />
            <Typography sx={{ color: theme.palette.text.secondary }}>
              Caricamento informazioni invito...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="error">{error}</Alert>
            <Button
              onClick={() => navigate('/login')}
              variant="outlined"
              fullWidth
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderColor: primaryColor,
                color: primaryColor,
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  bgcolor: `${primaryColor}10`,
                },
              }}
            >
              Torna al Login
            </Button>
          </Box>
        ) : inviteInfo ? (
          <Box
            component="form"
            onSubmit={handleAcceptInvite}
            sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            <Alert severity="info" sx={{ bgcolor: `${primaryColor}15` }}>
              Sei stato invitato come <strong>{getRoleLabel(inviteInfo.role)}</strong>
            </Alert>

            <TextField
              id="email"
              label="Email"
              type="email"
              value={inviteInfo.email}
              fullWidth
              disabled
              variant="outlined"
              slotProps={{ input: { sx: { borderRadius: '8px' } } }}
            />

            <TextField
              id="firstname"
              label="Nome"
              type="text"
              value={firstname}
              onChange={e => setFirstname(e.target.value)}
              placeholder="Mario"
              fullWidth
              required
              variant="outlined"
              slotProps={{ input: { sx: { borderRadius: '8px' } } }}
            />

            <TextField
              id="lastname"
              label="Cognome"
              type="text"
              value={lastname}
              onChange={e => setLastname(e.target.value)}
              placeholder="Rossi"
              fullWidth
              required
              variant="outlined"
              slotProps={{ input: { sx: { borderRadius: '8px' } } }}
            />

            <TextField
              id="password"
              label="Password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              fullWidth
              required
              variant="outlined"
              slotProps={{ input: { sx: { borderRadius: '8px' } } }}
            />

            {password.length > 0 && <PasswordRequirementsChecklist password={password} />}

            <TextField
              id="passwordConfirm"
              label="Conferma Password"
              type="password"
              value={passwordConfirm}
              onChange={e => setPasswordConfirm(e.target.value)}
              placeholder="••••••••"
              fullWidth
              required
              variant="outlined"
              slotProps={{ input: { sx: { borderRadius: '8px' } } }}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !validatePassword(password).valid || password !== passwordConfirm}
              fullWidth
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${primaryLightColor}, ${primaryColor})`,
                transition: 'filter 0.2s ease-in-out',
                '&:hover': {
                  filter: 'brightness(0.9)',
                },
              }}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Conferma Registrazione'
              )}
            </Button>
          </Box>
        ) : null}
      </GlassCard>
    </Box>
  );
};

export default InvitePage;
